import { api } from './api';

/** One signed-in session (GET /auth/sessions). */
export interface AccountSession {
  id: string;
  current: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  userAgent: string | null;
  ip: string | null;
  aal: string | null;
}

export interface RecoveryCodeStatus {
  remaining: number;
  createdAt: string | null;
}

/** Settings → Security: 2FA recovery codes and sessions (apps/api modules/auth/auth-security.controller.ts). */
export const securityService = {
  async createRecoveryCodes(): Promise<string[]> {
    const res = await api.post<{ codes: string[] }>('/auth/mfa/recovery-codes');
    return res.data.codes;
  },
  async recoveryCodeStatus(): Promise<RecoveryCodeStatus> {
    const res = await api.get<RecoveryCodeStatus>('/auth/mfa/recovery-codes');
    return res.data;
  },
  async deleteRecoveryCodes(): Promise<void> {
    await api.delete('/auth/mfa/recovery-codes');
  },
  /** Before the code step: spends a recovery code and turns 2FA off. */
  async recover(code: string): Promise<void> {
    await api.post('/auth/mfa/recover', { code });
  },
  async sessions(): Promise<AccountSession[]> {
    const res = await api.get<AccountSession[]>('/auth/sessions');
    return res.data;
  },
  async revokeSession(id: string): Promise<void> {
    await api.delete(`/auth/sessions/${encodeURIComponent(id)}`);
  },
};

/** "Chrome on Windows" from a user agent; null when nothing is recognised. Product names are not translated. */
export function describeUserAgent(ua: string | null | undefined): string | null {
  if (!ua) return null;
  const browser =
    /Edg\//.test(ua) ? 'Edge'
      : /OPR\/|Opera/.test(ua) ? 'Opera'
        : /SamsungBrowser/.test(ua) ? 'Samsung Internet'
          : /Firefox\//.test(ua) ? 'Firefox'
            : /Chrome\/|CriOS/.test(ua) ? 'Chrome'
              : /Safari\//.test(ua) ? 'Safari'
                : null;
  const os =
    /iPhone|iPad|iPod/.test(ua) ? 'iOS'
      : /Android/.test(ua) ? 'Android'
        : /Windows/.test(ua) ? 'Windows'
          : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
            : /CrOS/.test(ua) ? 'ChromeOS'
              : /Linux/.test(ua) ? 'Linux'
                : null;
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os;
}
